/**
 * UI Blocks for Chat - Renders ad cards, tables, charts, and other UI elements.
 *
 * This module supports two types of payloads in the `blocks` prop of `BlocksContainer`:
 *
 * 1. Array of Ad Objects (New flexible format)
 *    - If the array contains less than 3 ads, it renders an `AdCard` for each.
 *    - If the array contains 3 or more ads, it renders a single `AdsTable`.
 *
 * Ad object structure:
 * ```
 * {
 *   name: string,
 *   imageUrl: string,
 *   adType: 'image' | 'video',
 *   source?: string,       // Optional: for video ads
 *   metrics: {
 *     [key: string]: string | number
 *   },
 *   launchTime: string | Date
 * }
 * ```
 *
 * 2. Array of Block Objects (Legacy format for specific UI components)
 *    - Each object must have a `blockType` property.
 *    - Supported blockTypes: `adsChart`, `videoInsights`.
 *
 * `adsChart` block structure:
 * ```
 * {
 *   "blockType": "adsChart",
 *   "title": "Ads Performance",
 *   "metric": "ROAS",
 *   "chartType": "bar" | "pie",
 *   "ads": [...] // Array of ad-like objects with `title` and `metrics` array
 * }
 * ```
 *
 * `videoInsights` block structure:
 * ```
 * {
 *   "blockType": "videoInsights",
 *   "title": "Video Ad Title",
 *   "media": { "kind": "video", "thumbnailUrl": "...", "source": "..." },
 *   "retentionData": [...],
 *   "insights": [...],
 *   "duration": number
 * }
 * ```
 */

import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Icon } from "@iconify/react"
import { cn } from "@/lib/utils"
import { motion } from "framer-motion"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ReferenceLine } from 'recharts'
import { Slider } from "@/components/ui/slider"
import { fDate } from '@/utils/date'

// Helper to determine performance level based on ROAS
const getPerformanceLevel = (metrics) => {
    const roasMetric = metrics.find(m => m.label.toLowerCase() === 'roas')
    if (!roasMetric) return null

    const roas = typeof roasMetric.value === 'number' ? roasMetric.value : parseFloat(roasMetric.value)
    if (roas >= 4) return { level: 'excellent', color: 'success', icon: 'ph:trophy-fill' }
    if (roas >= 2.5) return { level: 'great', color: 'info', icon: 'ph:trending-up' }
    if (roas >= 1.5) return { level: 'good', color: 'warning', icon: 'ph:check-circle-fill' }
    // return { level: 'poor', color: 'error', icon: 'ph:warning-circle-fill' }
    return null
}

// Metric icon mapping
const getMetricIcon = (label) => {
    const iconMap = {
        'roas': 'ph:chart-line-up',
        'spend': 'ph:currency-dollar',
        'ctr': 'ph:cursor-click',
        'cpc': 'ph:hand-coins',
        'clicks': 'ph:mouse-simple',
        'aov': 'ph:shopping-cart',
        'impressions': 'ph:eye',
        'conversions': 'ph:chart-bar'
    }
    return iconMap[label.toLowerCase()] || 'ph:chart-line'
}

// AD CARD - Display single ad with metrics
export function AdCard({ ad }) {
    const { name: title, imageUrl, adType, source, metrics: metricsObj = {}, launchTime } = ad
    const media = {
        kind: adType,
        thumbnailUrl: imageUrl,
        source
    }
    const metrics = Object.entries(metricsObj).map(([label, value]) => ({ label, value }))
    const mediaIcon = media?.kind === 'video' ? 'ph:play-circle' : 'ph:image'
    // const performance = getPerformanceLevel(metrics)

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        // className="group"
        >
            <Card className="overflow-hidden border transition-all duration-300">
                <div className="flex flex-col lg:flex-row">
                    {/* Media Preview */}
                    {(media?.thumbnailUrl || media?.source) && (
                        <div className="relative w-full lg:w-64 h-48 lg:h-auto bg-gradient-to-br from-muted to-muted/50">
                            {/* Video */}
                            {media.kind === 'video' && media.source ? (
                                <video
                                    src={media.source}
                                    poster={media.thumbnailUrl}
                                    controls
                                    className="w-full h-full object-cover max-h-[250px] m-auto"
                                >
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                /* Image */
                                <img
                                    src={media.thumbnailUrl}
                                    alt={title}
                                    className="w-full h-full object-cover transition-transform duration-300 max-h-[250px] m-auto"
                                />
                            )}

                            {/* Gradient Overlay at Bottom */}
                            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

                            {/* Media Badge */}
                            <div className="absolute top-3 left-3">
                                <Badge variant={media.kind === 'video' ? 'default' : 'secondary'}>
                                    <Icon icon={mediaIcon} className="h-3 w-3 mr-1" />
                                    <span className="capitalize font-medium">{media.kind}</span>
                                </Badge>
                            </div>

                            {/* Performance Badge */}
                            {/* {performance && (
                                <div className="absolute top-3 right-3">
                                    <Badge
                                        variant="secondary"
                                        className={cn(
                                            "backdrop-blur-sm border shadow-md font-semibold text-xs px-2 py-0.5",
                                            performance.color === 'success' && "bg-success/90 text-success-foreground border-success/30",
                                            performance.color === 'info' && "bg-info/90 text-info-foreground border-info/30",
                                            performance.color === 'warning' && "bg-warning/90 text-warning-foreground border-warning/30",
                                            performance.color === 'error' && "bg-error/90 text-error-foreground border-error/30"
                                        )}
                                    >
                                        <Icon icon={performance.icon} className="h-3 w-3 mr-1" />
                                        <span className="capitalize">{performance.level}</span>
                                    </Badge>
                                </div>
                            )} */}
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 flex flex-col">
                        {/* Header with Gradient Accent */}
                        <div className="relative">
                            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_100%] animate-gradient" />

                            <CardHeader className="pb-3 px-4 pt-4">
                                <div className="flex items-start justify-between gap-2">
                                    <CardTitle className="text-base font-semibold truncate max-w-[300px]">
                                        {title}
                                    </CardTitle>
                                </div>
                                {launchTime && (
                                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                                        <Icon icon="ph:calendar" className="h-3.5 w-3.5" />
                                        <span>Launched on {fDate(launchTime)}</span>
                                    </div>
                                )}
                            </CardHeader>
                        </div>

                        {/* Metrics Grid */}
                        <CardContent className="flex-1 pt-0 px-4 pb-4">
                            {metrics.length > 0 && (
                                // <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                                <div className="flex flex-wrap gap-3">
                                    {metrics.map((metric, index) => {
                                        const metricIcon = getMetricIcon(metric.label)
                                        const labelLower = metric.label.toLowerCase()
                                        const isHighlight = labelLower === 'roas'

                                        const formatMetricValue = (value) => {
                                            if (typeof value !== 'number') {
                                                return value
                                            }
                                            const absValue = Math.abs(value)
                                            const shouldKeepFullPrecision = labelLower.includes('roas') || labelLower.includes('ctr')

                                            if (absValue >= 1_000_000) {
                                                const formatted = value / 1_000_000
                                                return `${formatted.toFixed(formatted >= 10 ? 1 : 2)}M`
                                            }
                                            if (absValue >= 1_000) {
                                                const formatted = value / 1_000
                                                return `${formatted.toFixed(formatted >= 10 ? 1 : 2)}K`
                                            }

                                            return value.toLocaleString(undefined, {
                                                maximumFractionDigits: shouldKeepFullPrecision ? 2 : 0,
                                                minimumFractionDigits: shouldKeepFullPrecision ? 2 : 0
                                            })
                                        }

                                        const formattedValue = formatMetricValue(metric.value)
                                        const isCompactValue = typeof formattedValue === 'string' && formattedValue.length >= 5

                                        return (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.05, duration: 0.2 }}
                                                className={cn(
                                                    "relative p-3 rounded-md border transition-all duration-200",
                                                    isHighlight
                                                        ? "bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20 hover:border-primary/40 hover:shadow-md"
                                                        : "bg-muted/30 border-border/50 hover:border-border hover:bg-muted/50"
                                                )}
                                            >
                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                    <Icon
                                                        icon={metricIcon}
                                                        className={cn(
                                                            "h-3.5 w-3.5",
                                                            isHighlight ? "text-primary" : "text-muted-foreground"
                                                        )}
                                                    />
                                                    <div className={cn(
                                                        "text-[10px] font-semibold uppercase tracking-wide",
                                                        isHighlight ? "text-primary" : "text-muted-foreground"
                                                    )}>
                                                        {metric.label}
                                                    </div>
                                                </div>
                                                <div className={cn(
                                                    "font-medium break-words",
                                                    isCompactValue ? "text-base leading-tight" : "text-md",
                                                    isHighlight && "bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"
                                                )}>
                                                    {formattedValue}
                                                </div>
                                            </motion.div>
                                        )
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </div>
                </div>
            </Card>
        </motion.div>
    )
}

// ADS TABLE - Display multiple ads in a table format
export function AdsTable({ ads: adsData, title = "Ads Overview" }) {
    // Adapt new ad structure to the one expected by the table's internal logic
    const ads = adsData.map(ad => ({
        title: ad.name,
        launchTime: ad.launchTime,
        media: {
            kind: ad.adType,
            thumbnailUrl: ad.imageUrl,
            source: ad.source,
        },
        metrics: Object.entries(ad.metrics || {}).map(([label, value]) => ({ label, value }))
    }))

    const [sortConfig, setSortConfig] = React.useState({ key: null, direction: null })
    const [isExpanded, setIsExpanded] = React.useState(false)

    // Handle column sort
    const handleSort = (metricLabel) => {
        let direction = 'asc'
        if (sortConfig.key === metricLabel && sortConfig.direction === 'asc') {
            direction = 'desc'
        }
        setSortConfig({ key: metricLabel, direction })
    }

    // Sort ads based on current sort config
    const sortedAds = React.useMemo(() => {
        if (!sortConfig.key) return ads

        return [...ads].sort((a, b) => {
            const aMetric = a.metrics?.find(m => m.label === sortConfig.key)
            const bMetric = b.metrics?.find(m => m.label === sortConfig.key)

            const aValue = typeof aMetric?.value === 'number' ? aMetric.value : parseFloat(aMetric?.value) || 0
            const bValue = typeof bMetric?.value === 'number' ? bMetric.value : parseFloat(bMetric?.value) || 0

            if (sortConfig.direction === 'asc') {
                return aValue - bValue
            } else {
                return bValue - aValue
            }
        })
    }, [ads, sortConfig])

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card className="overflow-hidden border">
                <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                        <Icon icon="ph:table" className="h-5 w-5 text-primary" />
                        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[60px]">Media</TableHead>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Launched</TableHead>
                                    <TableHead className="w-[100px]">Type</TableHead>
                                    {/* Dynamic metric headers - sortable */}
                                    {ads[0]?.metrics?.map((metric, index) => (
                                        <TableHead key={index} className="text-right">
                                            <button
                                                onClick={() => handleSort(metric.label)}
                                                className="flex items-center justify-end gap-1 w-full hover:text-foreground transition-colors"
                                            >
                                                <Icon icon={getMetricIcon(metric.label)} className="h-3.5 w-3.5" />
                                                <span>{metric.label}</span>
                                                {sortConfig.key === metric.label ? (
                                                    <Icon
                                                        icon={sortConfig.direction === 'asc' ? 'ph:caret-up-fill' : 'ph:caret-down-fill'}
                                                        className="h-3.5 w-3.5 text-primary"
                                                    />
                                                ) : (
                                                    <Icon
                                                        icon="ph:caret-up-down"
                                                        className="h-3.5 w-3.5 opacity-30"
                                                    />
                                                )}
                                            </button>
                                        </TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(isExpanded ? sortedAds : sortedAds.slice(0, 5)).map((ad, index) => (
                                    <TableRow
                                        key={index}
                                        className="group"
                                    >
                                        {/* Media Thumbnail */}
                                        <TableCell>
                                            {ad.media?.thumbnailUrl && (
                                                <div className="relative w-12 h-12 rounded-md overflow-hidden bg-muted">
                                                    <img
                                                        src={ad.media.thumbnailUrl}
                                                        alt={ad.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}
                                        </TableCell>

                                        {/* Title */}
                                        <TableCell className="font-medium max-w-[300px] truncate">
                                            {ad.title}
                                        </TableCell>

                                        {/* Launch Time */}
                                        <TableCell className="text-xs text-muted-foreground">
                                            {ad.launchTime ? fDate(ad.launchTime) : 'N/A'}
                                        </TableCell>

                                        {/* Type Badge */}
                                        <TableCell>
                                            <Badge variant={ad.media?.kind === 'video' ? 'default' : 'secondary'} className="text-xs">
                                                <Icon
                                                    icon={ad.media?.kind === 'video' ? 'ph:play-circle' : 'ph:image'}
                                                    className="h-3 w-3 mr-1"
                                                />
                                                <span className="capitalize">{ad.media?.kind || 'N/A'}</span>
                                            </Badge>
                                        </TableCell>

                                        {/* Metrics */}
                                        {ad.metrics?.map((metric, metricIndex) => {
                                            const isHighlight = metric.label.toLowerCase() === 'roas'
                                            return (
                                                <TableCell key={metricIndex} className="text-right">
                                                    <span className={cn(
                                                        "font-semibold",
                                                        isHighlight && "text-primary"
                                                    )}>
                                                        {typeof metric.value === 'number'
                                                            ? metric.value.toLocaleString(undefined, {
                                                                maximumFractionDigits: 2,
                                                                minimumFractionDigits: metric.label.toLowerCase().includes('roas') ? 2 : 0
                                                            })
                                                            : metric.value
                                                        }
                                                    </span>
                                                </TableCell>
                                            )
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    {sortedAds.length > 5 && (
                        <div className="p-3 border-t bg-muted/20 flex justify-center">
                            <button
                                onClick={() => setIsExpanded(!isExpanded)}
                                className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                            >
                                <Icon
                                    icon={isExpanded ? "ph:caret-up-bold" : "ph:caret-down-bold"}
                                    className="w-4 h-4"
                                />
                                {isExpanded ? "Show Less" : `Show All (${sortedAds.length})`}
                            </button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    )
}

// ADS CHART - Visualize ads with a single metric (bar or pie chart)
export function AdsChart({ block }) {
    const {
        title = "Ads Performance",
        ads = [],
        metric = 'ROAS',
        chartType = 'bar' // 'bar' or 'pie'
    } = block

    // Limit to 4 ads for bar, or show all for pie
    const limitedAds = chartType === 'bar' ? ads.slice(0, 4) : ads

    // Color palette for pie chart
    const COLORS = [
        'hsl(var(--primary))',
        'hsl(var(--secondary))',
        '#8b5cf6', // purple
        '#ec4899', // pink
        '#f97316', // orange
        '#10b981', // green
        '#3b82f6', // blue
        '#f59e0b', // amber
    ]

    // Transform data for recharts
    const chartData = limitedAds.map((ad, index) => {
        const metricData = ad.metrics?.find(m => m.label.toLowerCase() === metric.toLowerCase())
        const value = typeof metricData?.value === 'number' ? metricData.value : parseFloat(metricData?.value) || 0

        return {
            name: ad.title.length > 20 ? `${ad.title.substring(0, 20)}...` : ad.title,
            fullName: ad.title,
            value: value,
            fill: COLORS[index % COLORS.length]
        }
    })

    // Custom tooltip for bar chart
    const BarTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-background border rounded-lg shadow-lg p-3">
                    <p className="font-semibold text-sm mb-2">{payload[0]?.payload?.fullName || label}</p>
                    <div className="flex items-center gap-2 text-xs">
                        <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: payload[0]?.fill || 'hsl(var(--primary))' }}
                        />
                        <span className="text-muted-foreground">{metric}:</span>
                        <span className="font-semibold">
                            {typeof payload[0]?.value === 'number'
                                ? payload[0].value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                                : payload[0]?.value
                            }
                        </span>
                    </div>
                </div>
            )
        }
        return null
    }

    // Custom tooltip for pie chart
    const PieTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0]?.payload
            return (
                <div className="bg-background border rounded-lg shadow-lg p-3">
                    <p className="font-semibold text-sm mb-2">{data?.fullName}</p>
                    <div className="flex items-center gap-2 text-xs">
                        <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: data?.fill }}
                        />
                        <span className="text-muted-foreground">{metric}:</span>
                        <span className="font-semibold">
                            {typeof data?.value === 'number'
                                ? data.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                                : data?.value
                            }
                        </span>
                    </div>
                </div>
            )
        }
        return null
    }

    // Custom label for pie chart
    const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        const RADIAN = Math.PI / 180
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5
        const x = cx + radius * Math.cos(-midAngle * RADIAN)
        const y = cy + radius * Math.sin(-midAngle * RADIAN)

        if (percent < 0.05) return null // Don't show label if slice is too small

        return (
            <text
                x={x}
                y={y}
                fill="white"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                className="text-xs font-semibold"
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        )
    }

    const chartIcon = chartType === 'pie' ? 'ph:chart-pie-slice' : 'ph:chart-bar'

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card className="overflow-hidden border">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Icon icon={chartIcon} className="h-5 w-5 text-primary" />
                            <CardTitle className="text-lg font-semibold">{title}</CardTitle>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                            <Icon icon={getMetricIcon(metric)} className="h-3 w-3 mr-1" />
                            <span>{metric}</span>
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent>
                    {chartType === 'bar' ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={chartData}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis
                                    dataKey="name"
                                    className="text-xs fill-muted-foreground"
                                    tick={{ fontSize: 12 }}
                                />
                                <YAxis
                                    className="text-xs fill-muted-foreground"
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip content={<BarTooltip />} />
                                <Bar
                                    dataKey="value"
                                    fill="hsl(var(--primary))"
                                    radius={[4, 4, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={renderCustomLabel}
                                    outerRadius={100}
                                    fill="hsl(var(--primary))"
                                    dataKey="value"
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Pie>
                                <Tooltip content={<PieTooltip />} />
                                <Legend
                                    verticalAlign="bottom"
                                    height={36}
                                    formatter={(value, entry) => (
                                        <span className="text-xs text-muted-foreground">
                                            {entry.payload.name}
                                        </span>
                                    )}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    )
}

// Helper function to format time as MM:SS
const formatTime = (seconds) => {
    if (!seconds) return '00:00'
    const roundedValue = Math.round(seconds)
    const minutes = Math.floor(roundedValue / 60)
    const secs = roundedValue % 60
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

// VIDEO INSIGHTS CARD - Display video ad with watch retention chart
export function VideoInsightsCard({ block }) {
    const {
        title,
        media,
        retentionData = [],
        insights = [],
        duration = 30
    } = block

    const [currentTime, setCurrentTime] = React.useState(0)
    const videoRef = React.useRef(null)

    const handleSliderChange = (value) => {
        const newTime = value[0]
        setCurrentTime(newTime)
        if (videoRef.current) {
            videoRef.current.currentTime = newTime
        }
    }

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime)
        }
    }

    React.useEffect(() => {
        const videoEl = videoRef.current
        if (!videoEl) return

        videoEl.addEventListener('timeupdate', handleTimeUpdate)
        return () => {
            videoEl?.removeEventListener('timeupdate', handleTimeUpdate)
        }
    }, [media?.source])

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card className="overflow-hidden border">
                <div className="flex flex-col lg:flex-row">
                    {/* Video Preview */}
                    {(media?.thumbnailUrl || media?.source) && (
                        <div className="relative w-full lg:w-64 bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center p-4">
                            <div className="aspect-[9/16] w-full max-w-[200px] bg-muted rounded-lg overflow-hidden shadow-lg">
                                {media.kind === 'video' && media.source ? (
                                    <video
                                        ref={videoRef}
                                        src={media.source}
                                        poster={media.thumbnailUrl}
                                        controls
                                        playsInline
                                        className="w-full h-full object-cover"
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    <img
                                        src={media.thumbnailUrl}
                                        alt={title}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>

                            {/* Video Badge */}
                            <div className="absolute top-6 left-6">
                                <Badge variant="default">
                                    <Icon icon="ph:play-circle" className="h-3 w-3 mr-1" />
                                    <span className="capitalize font-medium">Video</span>
                                </Badge>
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 flex flex-col">
                        {/* Header */}
                        <div className="relative">
                            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_100%] animate-gradient" />

                            <CardHeader className="pb-3 px-4 pt-4">
                                <div className="flex items-start justify-between gap-2">
                                    <CardTitle className="text-base font-semibold">
                                        {title}
                                    </CardTitle>
                                    <Badge variant="secondary" className="text-xs">
                                        <Icon icon="ph:chart-line" className="h-3 w-3 mr-1" />
                                        Video Insights
                                    </Badge>
                                </div>
                            </CardHeader>
                        </div>

                        <CardContent className="flex-1 pt-0 px-4 pb-4 space-y-4">
                            {/* Watch Retention Chart */}
                            {retentionData.length > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Icon icon="ph:chart-line-up" className="h-4 w-4 text-primary" />
                                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                            Watch Retention
                                        </span>
                                    </div>
                                    <ResponsiveContainer width="100%" height={180}>
                                        <AreaChart data={retentionData}>
                                            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" opacity={0.3} />
                                            <XAxis
                                                dataKey="time"
                                                hide
                                                type="number"
                                                domain={[0, duration]}
                                            />
                                            <defs>
                                                <linearGradient id="fillRetention" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.1} />
                                                </linearGradient>
                                            </defs>

                                            {/* Current time indicator */}
                                            <ReferenceLine
                                                x={currentTime}
                                                stroke="hsl(var(--primary))"
                                                strokeWidth={2}
                                                strokeDasharray="3 3"
                                                ifOverflow="extendDomain"
                                            />

                                            <Tooltip
                                                content={({ active, payload }) => {
                                                    if (active && payload && payload.length) {
                                                        return (
                                                            <div className="bg-background border rounded-lg shadow-lg p-2">
                                                                <p className="text-xs">
                                                                    <span className="font-semibold">{payload[0].value}%</span> at {payload[0].payload.time}s
                                                                </p>
                                                            </div>
                                                        )
                                                    }
                                                    return null
                                                }}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="retention"
                                                stroke="hsl(var(--primary))"
                                                strokeWidth={2}
                                                fill="url(#fillRetention)"
                                                fillOpacity={0.4}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>

                                    {/* Slider Section */}
                                    <div className="ml-auto">
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-[9px] text-muted-foreground">00:00</span>
                                            <span className="text-[9px] text-muted-foreground">{formatTime(duration)}</span>
                                        </div>
                                        <Slider
                                            min={0}
                                            max={duration}
                                            step={0.1}
                                            value={[currentTime]}
                                            onValueChange={handleSliderChange}
                                            className="mt-1"
                                        />
                                    </div>

                                    {/* AI Insights below slider */}
                                    {insights.length > 0 && (
                                        <div className="mt-4 space-y-2 pt-4">
                                            <div className="flex items-center gap-2">
                                                <Icon icon="ph:sparkle" className="h-4 w-4 text-primary" />
                                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                                                    AI Insights
                                                </span>
                                            </div>
                                            <div className="space-y-2">
                                                {insights.map((insight, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: index * 0.1, duration: 0.2 }}
                                                        className="flex items-start gap-2 text-sm text-muted-foreground"
                                                    >
                                                        <Icon icon="ph:chat-circle-dots" className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                                                        <p className="leading-relaxed">{insight}</p>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </div>
                </div>
            </Card>
        </motion.div>
    )
}

// Block Renderer - renders a single block based on its blockType
export function BlockRenderer({ block }) {
    if (block.blockType === 'adCard') {
        // This is now handled by BlocksContainer directly for ad arrays.
        // Kept for potential legacy use, but should be deprecated.
        console.warn("Rendering adCard via BlockRenderer is deprecated.")
        return <AdCard block={block} />
    }

    if (block.blockType === 'adsTable') {
        // This is now handled by BlocksContainer directly for ad arrays.
        // Kept for potential legacy use, but should be deprecated.
        console.warn("Rendering adsTable via BlockRenderer is deprecated.")
        return <AdsTable block={block} />
    }

    if (block.blockType === 'adsChart') {
        return <AdsChart block={block} />
    }

    if (block.blockType === 'videoInsights') {
        return <VideoInsightsCard block={block} />
    }

    console.warn('Unknown block type:', block.blockType)
    return null
}

// Blocks Container - renders all blocks from a blocks response
export function BlocksContainer({ blocks }) {
    if (!blocks || blocks.length === 0) {
        return null
    }

    // Check if the payload is an array of ad objects (new format)
    const isAdArray = blocks[0] && 'name' in blocks[0] && 'imageUrl' in blocks[0] && !('blockType' in blocks[0])

    if (isAdArray) {
        // If it's an array of ads, decide whether to show cards or a table
        if (blocks.length < 3) {
            return (
                <div className="space-y-4">
                    {blocks.map((ad, index) => (
                        <AdCard key={index} ad={ad} />
                    ))}
                </div>
            )
        } else {
            return <AdsTable ads={blocks} />
        }
    }

    // Otherwise, assume it's an array of block objects (legacy format)
    return (
        <div className="space-y-4">
            {blocks.map((block, index) => (
                <BlockRenderer key={index} block={block} />
            ))}
        </div>
    )
}

